/**
 * Broker between the API layer and the re-auth modal.
 *
 * The backend's FreshAuthGuard answers `step_up_required` when a session has
 * not authenticated recently enough to move money. Resolving that is a UI job,
 * but apiClient must not import React. So the modal registers a handler here,
 * and apiClient asks the broker to run it.
 *
 * Concurrency matters: a settings page can fire several billing calls at once
 * and they would each pop their own modal. `inFlight` collapses them onto one
 * challenge, and every waiter resolves off that single result.
 */

let handler = null;
let inFlight = null;

/** Called by StepUpProvider on mount. Pass null on unmount. */
export function registerStepUpHandler(fn) {
  handler = fn;
}

/** True when a modal is actually mounted to service a challenge. */
export function canStepUp() {
  return typeof handler === 'function';
}

/**
 * Run the challenge, collapsing concurrent callers onto one prompt.
 * Resolves true when the user re-authenticated, false when they cancelled or
 * no handler is mounted.
 */
export function requestStepUp() {
  if (!handler) return Promise.resolve(false);
  if (inFlight) return inFlight;

  inFlight = Promise.resolve()
    .then(() => handler())
    .then(
      (ok) => {
        inFlight = null;
        return !!ok;
      },
      () => {
        inFlight = null;
        return false;
      },
    );

  return inFlight;
}

/** Test seam. */
export function __resetStepUpBroker() {
  handler = null;
  inFlight = null;
}
